import { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  deleteDoc,
  query,
  orderBy,
  limit
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

function TransactionList({ showAll = false }) {
  const [groupedData, setGroupedData] = useState({});
  const [user, setUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // ✅ separate loading states
  const [authLoading, setAuthLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);

  // 🔐 Auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false); // ✅ only auth here
    });

    return () => unsub();
  }, []);

  // 🔄 Firestore data
  useEffect(() => {
    if (!user) return;

    const baseRef = collection(db, "users", user.uid, "transactions");

    const q = showAll
      ? query(baseRef, orderBy("createdAt", "desc"))
      : query(baseRef, orderBy("createdAt", "desc"), limit(5));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const grouped = {};

      data.forEach((t) => {
        const date = new Date(t.createdAt.seconds * 1000).toDateString();

        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(t);
      });

      setGroupedData(grouped);
      setDataLoading(false); // ✅ only data here
    });

    return () => unsubscribe();
  }, [user, showAll]);

  // 🔥 Delete
  async function confirmDelete(id) {
    await deleteDoc(doc(db, "users", user.uid, "transactions", id));
    setDeleteId(null);
  }

  return (
  <div className="bg-[#111827]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl p-5 shadow-xl">

    <h2 className="text-lg font-semibold text-white mb-4">
      📜 Transactions
    </h2>

    {authLoading ? (
      <p className="text-slate-400 text-sm">Checking login...</p>

    ) : !user ? (
      <p className="text-slate-400 text-sm">
        Please login to see transactions
      </p>

    ) : dataLoading ? (
      <p className="text-slate-400 text-sm">
        Loading transactions...
      </p>

    ) : (
      <div className="space-y-5">

        {Object.keys(groupedData).map((date) => (
          <div key={date}>

            {/* Date */}
            <h3 className="text-xs text-slate-400 mb-2 uppercase tracking-wide">
              {date}
            </h3>

            {/* Transactions */}
            <div className="space-y-2">
              {groupedData[date].map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 hover:bg-slate-800 transition group"
                >

                  {/* LEFT */}
                  <div className="flex flex-col">
                    <span className="text-sm text-white font-medium">
                      {t.description}
                    </span>
                    <span className="text-xs text-slate-400">
                      {t.category}
                    </span>
                  </div>

                  {/* RIGHT */}
                  <div className="flex items-center gap-4">

                    {/* Amount */}
                    <span
                      className={`text-sm font-semibold ${
                        t.amount >= 0
                          ? "text-green-400"
                          : "text-red-400"
                      }`}
                    >
                      ₹{t.amount}
                    </span>

                    {/* Delete */}
                    {deleteId === t.id ? (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-red-400">Confirm?</span>
                        <button
                          onClick={() => confirmDelete(t.id)}
                          className="text-green-400 hover:text-green-300"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setDeleteId(null)}
                          className="text-slate-400 hover:text-white"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteId(t.id)}
                        className="text-red-400 hover:text-red-300 text-xs opacity-0 group-hover:opacity-100 transition"
                      >
                        Delete
                      </button>
                    )}

                  </div>

                </div>
              ))}
            </div>

          </div>
        ))}

      </div>
    )}

    {/* View All */}
    {!showAll && !authLoading && !dataLoading && (
      <button
        onClick={() => (window.location.href = "/all")}
        className="mt-5 w-full py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 text-sm hover:bg-slate-800 hover:border-green-500/30 transition"
      >
        View All Transactions →
      </button>
    )}

  </div>
);
}

export default TransactionList;